import { Component, computed, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { TarifOption } from '@mwe/models';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import ShortUniqueId from 'short-unique-id';
import { SetFocusDirective } from '../../../aria/set-focus/set-focus.directive';

@Component({
  selector: 'mwe-tariff-option-dropdown',
  templateUrl: './tariff-option-dropdown.component.html',
  imports: [NgbDropdown, NgbDropdownToggle, NgClass, NgbDropdownMenu, SetFocusDirective],
})
export class TariffOptionDropdownComponent {
  options = input<TarifOption[]>();
  selectedTariffOptionId = input<string>();
  ariaLabelledBy = input<string>(null);
  isReadonly = input<boolean>();
  selectedTariffGroupId = input<string>(null);
  selectedTariffKey = input<string>(null);
  setFocusRef = viewChild(SetFocusDirective);

  readonly optionSelected = output<string>();

  _selectedTariffOptionId = signal<string>('');
  _componentId = signal<string>(new ShortUniqueId({ dictionary: 'alpha' }).rnd(8));
  dropdownElement = viewChild<ElementRef<HTMLElement>>('dropdown');
  dropdownId = computed<string>(() => `${this._componentId()}_dropdown`);
  listboxElement = viewChild<ElementRef<HTMLElement>>('listbox');
  selectedOptionIndex = signal<number>(null);
  selectedOption = computed<HTMLElement>(() => {
    if (this.selectedOptionIndex() === null) return null;
    return this.listboxElement()?.nativeElement?.querySelectorAll('.dropdown-item')?.item(this.selectedOptionIndex()) as HTMLElement;
  });
  selectedTariffOption = computed<TarifOption>(() => {
    return this.options().find(o => this.selectedTariffOptionId() === o.option);
  });
  computedSelectedTariffOptionId = computed<string>(() => {
    return this._selectedTariffOptionId() || this.selectedTariffOptionId();
  });
  computedDataFocusKey = computed<string>(() => `${this.selectedTariffKey()}-${this.selectedTariffGroupId()}`);

  get colorCss(): string {
    switch (this.selectedTariffOption()?.farbe) {
      case 'green':
        return 'success';
      case 'blue':
        return 'secondary-light';
      default:
        return 'success';
    }
  }

  showDeltaUnit(option: TarifOption) {
    return option?.delta?.trim()?.length && option?.deltaUnit?.trim()?.length;
  }

  onOptionSelected(option: TarifOption) {
    this.setFocusRef()?.focus();
    this._selectedTariffOptionId.set(option.option);
    this.optionSelected.emit(this.computedSelectedTariffOptionId());
  }

  isOptionActive(option: TarifOption): boolean {
    return this.computedSelectedTariffOptionId() === option.option;
  }

  getTestId(type: string): string {
    return `tariff-option-dropdown-input-${type}`;
  }

  getOptionId(idx: string): string {
    if (idx === null) return null;
    return `${this._componentId()}_dropdown-option-${idx}`;
  }

  keyboardNavigation($event: KeyboardEvent): void {
    if (!this.options().length) {
      return;
    }

    switch ($event.key) {
      case 'Enter': {
        if (!this.selectedOption() || !this.options().length) {
          break;
        }
        this.onOptionSelected(this.options()[this.selectedOption().getAttribute('data-suggestion-index')]);
        break;
      }
      case 'Escape': {
        this.selectedOptionIndex.set(null);
        break;
      }
      case 'ArrowDown': {
        if (!this.selectedOption()) {
          const firstOption = this.dropdownElement().nativeElement.querySelector('.dropdown-item') as HTMLElement;
          this.selectOption(firstOption);
          break;
        }
        const nextElement = this.selectedOption().nextSibling as HTMLElement;
        if (
          nextElement &&
          nextElement.nodeType === 1 &&
          this.selectedOption().classList.contains('dropdown-item') &&
          nextElement.classList.contains('dropdown-item')
        ) {
          this.selectOption(nextElement);
        }
        break;
      }
      case 'ArrowUp': {
        const prevElement = this.selectedOption().previousSibling as HTMLElement;
        if (
          prevElement &&
          prevElement.nodeType === 1 &&
          this.selectedOption().classList.contains('dropdown-item') &&
          prevElement.classList.contains('dropdown-item')
        ) {
          this.selectOption(prevElement);
        }
        break;
      }
    }
  }

  selectOption(element: HTMLElement): void {
    const siblings = n => [...n.parentElement.children].filter(c => c.nodeType == 1 && c != n);
    element.classList.add('dropdown-item-selected');
    siblings(element).forEach(e => e.classList.remove('dropdown-item-selected'));
    this.selectedOptionIndex.set(parseInt(element.getAttribute('data-suggestion-index'), 10));
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}
